import React, { useRef, useMemo } from 'react';
import { BufferGeometry, BufferAttribute, LineBasicMaterial } from 'three';

/**
 * StockTrail - TRON-style glowing path showing stock movement history
 * Renders a luminous trail from start position to current timeStep
 * Trail gets brighter (more opaque) as it gets more recent
 * Creates a beautiful visual of the stock's journey through 3D space
 */
export const StockTrail = ({ stock, timeStep = 0, showTrails = true }) => {
  const lineRef = useRef();

  const trailGeometry = useMemo(() => {
    if (!showTrails || !stock.trajectory || timeStep < 1) {
      return null;
    }

    // Get all points from start to current timeStep
    const points = stock.trajectory.slice(0, timeStep + 1);

    if (points.length < 2) {
      return null;
    }

    // Create positions array for line
    const positions = new Float32Array(points.length * 3);
    points.forEach((point, i) => {
      positions[i * 3] = point.x;
      positions[i * 3 + 1] = point.y;
      positions[i * 3 + 2] = point.z;
    });

    // Create colors array - TRON style: dim glow to bright glow
    const colors = new Float32Array(points.length * 3);
    const baseColor = {
      r: parseInt(stock.color.slice(1, 3), 16) / 255,
      g: parseInt(stock.color.slice(3, 5), 16) / 255,
      b: parseInt(stock.color.slice(5, 7), 16) / 255,
    };

    points.forEach((point, i) => {
      // Fade from almost invisible to full brightness
      const intensity = 0.2 + (i / points.length) * 0.8; // 0.2 to 1.0

      colors[i * 3] = baseColor.r * intensity;
      colors[i * 3 + 1] = baseColor.g * intensity;
      colors[i * 3 + 2] = baseColor.b * intensity;
    });

    // Create geometry
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setAttribute('color', new BufferAttribute(colors, 3));

    return geometry;
  }, [stock, timeStep, showTrails]);

  if (!trailGeometry) {
    return null;
  }

  return (
    <line ref={lineRef} geometry={trailGeometry}>
      <lineBasicMaterial
        attach="material"
        color={stock.color}
        linewidth={3}
        vertexColors={true}
        transparent={true}
        opacity={0.8}
        fog={false}
        toneMapped={false}
      />
    </line>
  );
};

export default StockTrail;
